var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { getNoteName, isBlackKey } from '../utils/Notes';
import { generateGraphDataFromChord } from '../utils/Chords';
import PreviewControls from './PreviewControls';
import musicI18n from '../locale';
import moduleStyles from './chordPanel.module.scss';
var NUM_OCTAVES = 3;
var START_OCTAVE = 4;
var MAX_NOTES = 16;
var styleDropdownOptions = [
    ['arpeggio-up', musicI18n.chordArpeggioUp()],
    ['arpeggio-down', musicI18n.chordArpeggioDown()],
    ['arpeggio-random', musicI18n.chordArpeggioRandom()],
    ['together', musicI18n.chordTogether()],
];
var ChordPanel = function (_a) {
    var initValue = _a.initValue, onChange = _a.onChange, previewChord = _a.previewChord, previewNote = _a.previewNote, cancelPreviews = _a.cancelPreviews, library = _a.library;
    var _b = useState(initValue.notes), selectedNotes = _b[0], setSelectedNotes = _b[1];
    var _c = useState(initValue.playStyle), playStyle = _c[0], setPlayStyle = _c[1];
    var _d = useState(initValue.instrument), instrument = _d[0], setInstrument = _d[1];
    var _e = useState(selectedNotes.length >= MAX_NOTES), isDisabled = _e[0], setIsDisabled = _e[1];
    var instruments = library.groups[0].folders
        .filter(function (folder) { return folder.type === 'instrument'; })
        .map(function (folder) { return [folder.name, folder.path]; });
    var onPressKey = useCallback(function (note) {
        var newSelectedNotes = __spreadArray([], selectedNotes, true);
        if (newSelectedNotes.includes(note)) {
            newSelectedNotes.splice(newSelectedNotes.indexOf(note), 1);
        }
        else {
            newSelectedNotes.push(note);
            previewNote(note, instrument);
        }
        setSelectedNotes(newSelectedNotes);
    }, [selectedNotes, instrument, setSelectedNotes, previewNote]);
    useEffect(function () {
        onChange({
            notes: selectedNotes,
            playStyle: playStyle,
            instrument: instrument,
        });
    }, [instrument, playStyle, selectedNotes, onChange]);
    useEffect(function () {
        setIsDisabled(selectedNotes.length >= MAX_NOTES);
    }, [selectedNotes]);
    var playPreview = useCallback(function () {
        return previewChord({
            notes: selectedNotes,
            playStyle: playStyle,
            instrument: instrument,
        });
    }, [previewChord, selectedNotes, playStyle, instrument]);
    var onClear = useCallback(function () { return setSelectedNotes([]); }, [setSelectedNotes]);
    return (React.createElement("div", { className: moduleStyles.chordPanelContainer },
        React.createElement("div", { className: moduleStyles.optionsRow },
            React.createElement("select", { value: instrument, onChange: function (event) { return setInstrument(event.target.value); }, className: moduleStyles.dropdown }, instruments.map(function (_a) {
                var name = _a[0], value = _a[1];
                return (React.createElement("option", { key: value, value: value }, name));
            })),
            React.createElement("select", { value: playStyle, onChange: function (event) { return setPlayStyle(event.target.value); }, className: moduleStyles.dropdown }, styleDropdownOptions.map(function (_a) {
                var playStyle = _a[0], label = _a[1];
                return (React.createElement("option", { key: playStyle, value: playStyle }, label));
            }))),
        React.createElement(Keybed, { numOctaves: NUM_OCTAVES, startOctave: START_OCTAVE, selectedNotes: selectedNotes, onPressKey: onPressKey, isDisabled: isDisabled }),
        React.createElement(NoteGrid, { numOctaves: NUM_OCTAVES, startOctave: START_OCTAVE, selectedNotes: selectedNotes, playStyle: playStyle, instrument: instrument }),
        React.createElement(PreviewControls, { enabled: selectedNotes.length > 0, playPreview: playPreview, onClickClear: onClear, cancelPreviews: cancelPreviews })));
};
var Keybed = function (_a) {
    var numOctaves = _a.numOctaves, startOctave = _a.startOctave, selectedNotes = _a.selectedNotes, onPressKey = _a.onPressKey, isDisabled = _a.isDisabled;
    var keys = [];
    var startingNote = startOctave * 12;
    var _loop_1 = function (currentNote) {
        keys.push(React.createElement(Key, { key: currentNote, type: isBlackKey(currentNote) ? 'black' : 'white', isDisabled: isDisabled, isSelected: selectedNotes.includes(currentNote), onClick: function () { return onPressKey(currentNote); }, text: !isBlackKey(currentNote) ? getNoteName(currentNote) : undefined }));
    };
    for (var currentNote = startingNote; currentNote < startingNote + numOctaves * 12; currentNote++) {
        _loop_1(currentNote);
    }
    return (React.createElement("div", { id: "keypad", className: moduleStyles.keybed }, keys));
};
var Key = function (_a) {
    var type = _a.type, isSelected = _a.isSelected, isDisabled = _a.isDisabled, onClick = _a.onClick, text = _a.text;
    return (React.createElement("div", { className: classNames(moduleStyles.key, isDisabled && moduleStyles.disabled, isSelected && moduleStyles.selected, type === 'white' && moduleStyles.whiteKey, type === 'black' && moduleStyles.blackKey), onClick: isSelected || !isDisabled ? onClick : undefined },
        React.createElement("div", { className: moduleStyles.noteLabel }, text)));
};
var NoteGrid = function (_a) {
    var numOctaves = _a.numOctaves, startOctave = _a.startOctave, selectedNotes = _a.selectedNotes, playStyle = _a.playStyle, instrument = _a.instrument;
    var graphNotes = generateGraphDataFromChord({
        chordEventValue: {
            notes: selectedNotes,
            playStyle: playStyle,
            instrument: instrument,
        },
        width: 315,
        height: 110,
        numOctaves: numOctaves,
        startOctave: startOctave,
        padding: 2,
        noteHeightScale: 2,
    });
    return (React.createElement("div", { id: "notegrid-container", className: moduleStyles.noteGridContainer }, graphNotes.map(function (graphNote, index) {
        return (React.createElement("div", { key: index, className: moduleStyles.gridNote, style: {
                top: graphNote.y,
                left: graphNote.x,
                width: graphNote.width,
                height: graphNote.height,
            } }, "\u00A0"));
    })));
};
export default ChordPanel;
